import { useEffect, useState } from 'react';
import {
  all,
  path,
  isNil,
  isEmpty,
  filter,
  values,
  keys,
  difference,
  pick,
} from 'ramda';
import { Auth, Storage } from 'aws-amplify';

import { ReportParams } from 'scenes/Report/components/ReportBase';

import { ReportConfig, reportsConfig } from '../BatchReport/BatchReport.config';
import { getUserIdentityId } from 'scenes/Report/Report.helper';

type ReportsData = {
  [key: string]: Record<string, unknown>;
};

type Reports = {
  invalidReports?: Record<string, unknown>[];
  validReports?: {
    [key: string]: Record<string, unknown>;
  };
};

const filterBadReports = (reportsData: ReportsData) => ({
  queries,
}: ReportConfig & any) =>
  all((query: string) => {
    const data = Array.isArray(query)
      ? path(query, reportsData)
      : reportsData[query];
    return isNil(data) || isEmpty(data);
  }, queries);

const getReportsWithNoData = (reportsData: ReportsData) => {
  const reports = filter(filterBadReports(reportsData), reportsConfig);

  return {
    invalidReports: values(reports),
    invalidReportsKeys: keys(reports),
  };
};

const setS3File = async (
  { merchantName, startDate, endDate, jobUuid }: ReportParams,
  setReportsData: React.Dispatch<React.SetStateAction<ReportsData | null>>,
) => {
  const credentials = await Auth.currentUserCredentials();
  const file = await Storage.get(
    `private/${getUserIdentityId(
      credentials,
    )}/reports_data/${merchantName}/${startDate}-${endDate}/${jobUuid}.json`,
    {
      bucket: process.env.REPORTS_BUCKET,
      download: true,
      ContentType: 'application/json',
      region: process.env.AWS_REGION,
    },
  );
  file.Body.text().then((string: string) => {
    setReportsData(JSON.parse(string));
  });
};

export const useGetReportsData = (
  params: ReportParams,
  onCompleted: (validReportList: string[]) => void,
): Record<string, any> => {
  const [loading, setLoading] = useState<boolean>(!!params.jobUuid);
  const [reportsData, setReportsData] = useState<ReportsData | null>(null);
  const [validReportList, setValidReportList] = useState<string[]>([]);
  const [reports, setReports] = useState<Reports>({});

  useEffect(() => {
    if (loading) {
      setS3File(params, setReportsData);
    }
  }, []);

  useEffect(() => {
    if (reportsData) {
      const { invalidReports, invalidReportsKeys } = getReportsWithNoData(
        reportsData,
      );

      const vReportList = difference(
        params.reportList,
        invalidReportsKeys,
      ) as string[];

      const validReports = pick(vReportList, reportsConfig);

      setValidReportList(vReportList);
      setReports({ validReports, invalidReports });
      setLoading(false);
      onCompleted(validReportList);
    }
  }, [reportsData]);

  return {
    loading,
    validReportList,
    reportsData,
    reports,
  };
};
